import { createContext, useContext, useEffect, useRef } from 'react';
import {
  IApp,
  ILauncherProvider,
  ILauncherProviderContext,
  ILaunchStrategy,
} from 'types';
import { BaseStrategy } from 'lib/launchStrategies/launchStrategies';
import { selectStrategy } from 'lib/launchStrategies/helpers';
import { errorGenerator } from 'lib/errorGenerator';
import { generatoreCode } from 'lib/generateCode';
import i18n from 'i18n';
import { RQX_ACTIVE_APPLINK } from 'lib/config';
import { SSO_LOCAL_STORAGE_KEY_REALM } from 'lib/config';

/**
 * mette a disposizione dell'applicazione i metodi per lanciare un'altra applicazione
 */

/**
 * context
 */
const LauncherProviderContext = createContext<ILauncherProviderContext>({
  launchApplication: (data: IApp) => {},
});

/**
 * provider
 */
const LauncherStrategyProvider = ({ children }: ILauncherProvider) => {
  // const [currentAppData, setCurrentAppData] = useState<IApp | null>(null);
  // const kc = useKeycloak();
  // const { hasRole } = useInitProvider();
  const currentAppData = useRef<IApp | null>();
  console.log('LauncherStrategyProvider currentAppData', JSON.stringify(currentAppData));

  const baseStrategy: ILaunchStrategy = new BaseStrategy();

  const sendPostMessagesAfterAppLaunch = (app: IApp) => {
    const i = document.getElementById('app-iframe') as HTMLIFrameElement;
    if (i) {
      const locale = localStorage.getItem('i18nextLng') || i18n.language;
      console.log(`[send post after launch] locale: ${locale}`);
      i.contentWindow.postMessage({ locale }, '*');
      const realm = resolveRealm();
      if (!realm) {
        console.error('Realm not found in local storage');
      } else {
        console.log(`[send post after launch] realm: ${realm}`);
        i.contentWindow.postMessage({ realm }, '*');
      }
      const appCode = app.code;
      const deeplinkDetails = app.deepLinkDetails;
      console.log(`[send post after launch] "${appCode}" deeplinkDetails: ${JSON.stringify(deeplinkDetails)}`);
      const hash = window.location.hash;
      if (deeplinkDetails) {
        const appLink = `${appCode}/${deeplinkDetails}`
        console.log(`[send post after launch] "${appCode}" deep link detected, postMessage: {"route":"${appLink}"}`);
        console.log(`Saving local storage "${RQX_ACTIVE_APPLINK} appLink`)
        localStorage.setItem(RQX_ACTIVE_APPLINK, appLink);
        i.contentWindow.postMessage({ deeplinkDetails }, '*');
        i.contentWindow.postMessage({ route: appLink }, '*');
      } else {
        console.log(`[send post after launch] no deep link, skipping postMessage appRoute for hash "${hash}"`);
      }
    } else {
      console.error('Iframe not found (element "app-iframe")');
    }
  };

  // exposed method
  const launch = async (data: IApp) => {
    currentAppData.current = data;
    try {
      // select the right strategy based on data
      const strategy = selectStrategy({
        data,
        token: 'REMOVE-ME!.keycloak.token',
      });
      // console.log(`launch strategy=${strategy}, data=${JSON.stringify(data)}`)
      baseStrategy.setStrategy(strategy);
      // launch the app (IFrameDocStrategy.launch è async: attendere prima dei postMessage verso l'iframe)
      await Promise.resolve(baseStrategy.launch(data));

      sendPostMessagesAfterAppLaunch(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Error:', message);
      const errorCode = generatoreCode(8).toString();
      errorGenerator(
        errorCode,
        error as Error,
        'Fail launch application',
        'Tentativo di far partire l applicativo all interno dell iframe',
      );
    }
  };

  const gotoCurrentAppHome = () => {
    currentAppData.current && launch(currentAppData.current);
  };

  const contextValue = {
    gotoCurrentAppHome,
    launchApplication: launch,
  };

  return (
    <LauncherProviderContext.Provider value={contextValue}>
      {children}
    </LauncherProviderContext.Provider>
  );
};

/**
 * custom hook
 */
export function useLauncherStrategyProvider() {
  const ctx = useContext(LauncherProviderContext);
  if (!ctx) console.log('LauncherProviderContext cannot be undefined!');
  return ctx;
}

export default LauncherStrategyProvider;
function resolveRealm() {
  const realm = localStorage.getItem(SSO_LOCAL_STORAGE_KEY_REALM);
  if (realm) {
    console.error('Realm not found in local storage');
    return realm;
  }
  // get from cookie rqx_realm
  const cookie = document.cookie.split(';').find(c => c.trim().startsWith('rqx_realm='));
  let tokens;
  if (cookie) {
    tokens = cookie.split('=');
    if (tokens.length > 1) {
      return tokens[1];
    }
  }
  // get from cookie rqx_lastlogin
  const lastlogin = document.cookie.split(';').find(c => c.trim().startsWith('rqx_lastlogin='));
  if (lastlogin) {
    tokens = lastlogin.split('=');
    if (tokens.length > 1) {
      return tokens[1];
    }
  }
  return null;
}

